import { useCallback, useState } from 'react';
import type { VehicleMergeResult } from '@/types/vehicle';
import { DuplicatePlateMergeDialog } from '../components/DuplicatePlateMergeDialog';
import {
    getDuplicatePlateConflict,
    type DuplicatePlateConflict,
} from '../utils/duplicatePlate';

interface DialogState {
    sourceVehicleId: string;
    conflict: DuplicatePlateConflict;
}

interface UseDuplicatePlateMergeOptions {
    onMerged: (result: VehicleMergeResult) => void;
    onSourceStale?: () => void;
}

interface UseDuplicatePlateMergeResult {
    handleUpdateError: (error: unknown, sourceVehicleId: string) => boolean;
    dialog: React.ReactNode;
}

export function useDuplicatePlateMerge({
    onMerged,
    onSourceStale,
}: UseDuplicatePlateMergeOptions): UseDuplicatePlateMergeResult {
    const [state, setState] = useState<DialogState | null>(null);

    const handleUpdateError = useCallback(
        (error: unknown, sourceVehicleId: string): boolean => {
            const conflict = getDuplicatePlateConflict(error);
            if (!conflict) {
                return false;
            }
            setState({ sourceVehicleId, conflict });
            return true;
        },
        [],
    );

    const close = useCallback(() => setState(null), []);

    const dialog = state ? (
        <DuplicatePlateMergeDialog
            isOpen
            sourceVehicleId={state.sourceVehicleId}
            conflict={state.conflict}
            onCancel={close}
            onMerged={(result) => {
                close();
                onMerged(result);
            }}
            onSourceStale={() => {
                close();
                onSourceStale?.();
            }}
        />
    ) : null;

    return { handleUpdateError, dialog };
}
